import { Controller } from "@hotwired/stimulus"
import Choices from "choices.js"

// Connects to data-controller="tags"
export default class extends Controller {
  static targets = ["select", "hiddenField"]
  static values = {
    selectedTags: { type: Array, default: [] },
    searchUrl: String,
    fieldName: String,
    allowCreate: { type: Boolean, default: true },
    placeholderText: { type: String, default: "Select or create tags..." },
    noChoicesText: { type: String, default: "Type to search for tags..." }
  }

  connect() {
    // Track selected names locally to exclude from search results
    this.selectedNames = new Set(this.selectedTagsValue.map(tag => tag.name))

    this.choices = new Choices(this.selectTarget, {
      removeItemButton: true,
      duplicateItemsAllowed: false,
      addItems: this.allowCreateValue,
      addItemText: (value) => `Press Enter to add "${value}"`,
      searchEnabled: true,
      placeholder: true,
      placeholderValue: this.placeholderTextValue,
      searchFloor: 1,
      searchResultLimit: 20,
      noChoicesText: this.noChoicesTextValue,
      searchChoices: false // Disable client-side filtering, we use server-side search
    })

    // Add selected tags as pre-selected choices
    if (this.selectedTagsValue.length > 0) {
      const selectedChoices = this.selectedTagsValue.map(tag => ({
        value: tag.name,
        label: tag.name,
        selected: true
      }))
      this.choices.setChoices(selectedChoices, "value", "label", false)
    }

    this._handleSearch = this.handleSearch.bind(this)
    this._handleAddItem = this.handleAddItem.bind(this)
    this._handleRemoveItem = this.handleRemoveItem.bind(this)

    this.selectTarget.addEventListener("search", this._handleSearch)
    this.selectTarget.addEventListener("addItem", this._handleAddItem)
    this.selectTarget.addEventListener("removeItem", this._handleRemoveItem)

    this.syncHiddenFields()
  }

  disconnect() {
    if (this.choices) {
      this.selectTarget.removeEventListener("search", this._handleSearch)
      this.selectTarget.removeEventListener("addItem", this._handleAddItem)
      this.selectTarget.removeEventListener("removeItem", this._handleRemoveItem)
      this.choices.destroy()
    }
  }

  handleSearch(event) {
    const query = event.detail.value

    if (query.length < 1) {
      this.choices.setChoices([], "value", "label", true)
      return
    }

    const excludeNames = Array.from(this.selectedNames).join(",")
    const url = `${this.searchUrlValue}?q=${encodeURIComponent(query)}&exclude=${excludeNames}`

    fetch(url, {
      headers: {
        "Accept": "application/json",
        "X-CSRF-Token": this.csrfToken
      }
    })
    .then(response => response.json())
    .then(choices => {
      if (this.allowCreateValue) {
        // Check if query exactly matches an existing choice (case-insensitive)
        const exactMatch = choices.some(c => c.value.toLowerCase() === query.toLowerCase())

        // If no exact match then show the new tag
        if (!exactMatch && !this.selectedNames.has(query)) {
          choices.unshift({
            value: query,
            label: query,
            customProperties: { isNew: true }
          })
        }
      }

      this.choices.setChoices(choices, "value", "label", true)
    })
  }

  handleAddItem(event) {
    const tagName = event.detail.value
    this.selectedNames.add(tagName)
    this.syncHiddenFields()
  }

  handleRemoveItem(event) {
    const tagName = event.detail.value
    this.selectedNames.delete(tagName)
    this.syncHiddenFields()
  }

  syncHiddenFields() {
    const selectedValues = this.choices.getValue(true)

    // Clear existing hidden fields
    this.hiddenFieldTargets.forEach(field => field.remove())

    // Create hidden fields for each selected tag
    selectedValues.forEach(value => {
      const input = document.createElement("input")
      input.type = "hidden"
      input.name = this.fieldNameValue
      input.value = value
      input.dataset.tagsTarget = "hiddenField"
      this.element.appendChild(input)
    })
  }

  get csrfToken() {
    return document.querySelector('meta[name="csrf-token"]')?.content
  }
}
